// 导航菜单工具
import {
  isProductCatalogTemplateId,
  resolveMenuTemplateId,
  resolveTemplateId
} from '@/components/portal/common/utils/menuTemplateRegistry'
import { isPathUnderMenu, normalizeMenuPath as normalizeMenuNodePath, resolveMenuNodePath } from '@/components/portal/common/utils/menuPath'
import { isLocaleHomePath, stripLocalePrefix } from '@/components/portal/common/utils/localePath'
import { isPortalMenuVisible } from '@/components/portal/common/utils/portalMenuVisibility'

export const HOME_PATH = '/'

function normalizePath(path) {
  if (!path) return ''
  const trimmed = String(path).trim()
  if (!trimmed) return ''
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

/** 判断菜单项是否为首页 */
export function isHomeMenuItem(item) {
  return normalizePath(item?.path) === HOME_PATH
}

/** 递归收集菜单树中所有路径 */
function collectPaths(items = []) {
  const paths = []
  for (const item of items) {
    const path = normalizePath(item.path)
    if (path) paths.push(path)
    paths.push(...collectPaths(item.children))
  }
  return paths
}

/** 递归查找菜单树中第一个有效叶子路径 */
function findFirstLeafPath(items = []) {
  for (const item of items) {
    const path = normalizePath(item.path)
    if (path) return path
    const nested = findFirstLeafPath(item.children)
    if (nested) return nested
  }
  return ''
}

/** 解析子菜单项的路由路径（自身路径或首个叶子路径） */
function resolveChildPath(item) {
  return normalizePath(item.path) || findFirstLeafPath(item.children) || ''
}

/** 构建菜单路径匹配正则（含子路径前缀匹配） */
function buildMatch(path, children = []) {
  const basePath = normalizePath(path)
  if (basePath === HOME_PATH) return /^\/$/

  const paths = [...new Set([basePath, ...collectPaths(children)].filter(Boolean))]
  if (!paths.length) return /^$/

  const pattern = paths
    .map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')

  return new RegExp(`^(${pattern})(/|$)`)
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value != null && String(value).trim() !== '') return value
  }
  return ''
}

export function resolveLocaleStyleField(item, key, locale, defaultLocale = 'zh-CN') {
  const overlay = locale ? item?.localeStyles?.[locale]?.[key] : null
  const fallback = defaultLocale ? item?.localeStyles?.[defaultLocale]?.[key] : null
  return firstNonEmpty(overlay, fallback, item?.[key])
}

export function resolveMenuSummary(item, locale, defaultLocale = 'zh-CN') {
  const direct = String(item?.summary || '').trim()
  if (direct) return direct
  const map = item?.summaries
  if (!map || typeof map !== 'object') return ''
  if (locale && map[locale] != null && String(map[locale]).trim()) {
    return String(map[locale]).trim()
  }
  if (defaultLocale && map[defaultLocale] != null) {
    return String(map[defaultLocale]).trim()
  }
  return ''
}

export function resolveMenuLabel(item, locale, defaultLocale = 'zh-CN') {
  const map = item?.names
  if (map && typeof map === 'object') {
    if (locale && map[locale] != null && String(map[locale]).trim()) {
      return String(map[locale]).trim()
    }
    if (defaultLocale && map[defaultLocale] != null && String(map[defaultLocale]).trim()) {
      return String(map[defaultLocale]).trim()
    }
  }
  return String(item?.name || '').trim()
}

function resolveMenuBannerFields(item, locale) {
  return {
    bannerUrl: resolveLocaleStyleField(item, 'bannerUrl', locale),
    nameColor: resolveLocaleStyleField(item, 'nameColor', locale),
    nameFontSize: resolveLocaleStyleField(item, 'nameFontSize', locale),
    summaryColor: resolveLocaleStyleField(item, 'summaryColor', locale),
    summaryFontSize: resolveLocaleStyleField(item, 'summaryFontSize', locale),
  }
}

/** 转换子菜单项为导航所需结构 */
function transformChild(item) {
  return {
    id: item.id,
    name: item.name,
    path: resolveChildPath(item),
    children: item.children || []
  }
}

/** 转换顶级菜单项为导航结构（含 match 正则与 defaultPath） */
function transformTopMenu(item, locale) {
  const bannerFields = resolveMenuBannerFields(item, locale)
  if (isHomeMenuItem(item)) {
    return {
      key: String(item.id),
      label: resolveMenuLabel(item, locale),
      defaultPath: HOME_PATH,
      match: /^\/$/,
      children: [],
      isHome: true,
      bannerUrl: bannerFields.bannerUrl || '',
      summary: resolveMenuSummary(item, locale),
      nameColor: bannerFields.nameColor || '',
      nameFontSize: bannerFields.nameFontSize || '',
      summaryColor: bannerFields.summaryColor || '',
      summaryFontSize: bannerFields.summaryFontSize || '',
      enabled: item.enabled,
      portalVisible: item.portalVisible,
      portalVisibilities: item.portalVisibilities
    }
  }
  const children = (item.children || [])
    .filter((child) => isPortalMenuVisible(child, locale))
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map(transformChild)

  const path = normalizePath(item.path)
  const defaultPath = path || children[0]?.path || ''

  return {
    key: String(item.id),
    label: resolveMenuLabel(item, locale),
    defaultPath,
    match: buildMatch(resolveMenuNodePath(item) || item.path, item.children),
    children,
    isHome: false,
    bannerUrl: bannerFields.bannerUrl || '',
    summary: resolveMenuSummary(item, locale),
    nameColor: bannerFields.nameColor || '',
    nameFontSize: bannerFields.nameFontSize || '',
    summaryColor: bannerFields.summaryColor || '',
    summaryFontSize: bannerFields.summaryFontSize || '',
    contentType: item.contentType || '',
    templateId: resolveMenuTemplateId(item, locale),
    enabled: item.enabled,
    portalVisible: item.portalVisible,
    portalVisibilities: item.portalVisibilities
  }
}

/** 排序导航菜单（首页始终排第一） */
function sortNavMenus(menus) {
  return [...menus].sort((a, b) => {
    if (a.isHome) return -1
    if (b.isHome) return 1
    return 0
  })
}

/** 将后台门户菜单转换为前端导航菜单结构（含门户隐藏项，供横幅与路由匹配） */
export function transformPortalMenus(menus = [], locale) {
  const transformed = [...menus]
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((item) => transformTopMenu(item, locale))

  return sortNavMenus(transformed)
}

/** 根据当前路径查找匹配的导航菜单项 */
export function findMenuByPath(menus, path) {
  const contentPath = stripLocalePrefix(path)
  if (isLocaleHomePath(path) || contentPath === HOME_PATH) {
    return menus.find((menu) => menu.isHome) || null
  }
  return menus.find((menu) => menu.match.test(contentPath)) || null
}

/** 在原始菜单树中按 id 递归查找菜单节点 */
export function findRawMenuNodeById(menus = [], menuId) {
  if (menuId == null || menuId === '' || !menus.length) return null
  const target = String(menuId)

  // walk
  const walk = (nodes = []) => {
    for (const node of nodes) {
      if (String(node.id) === target) return node
      const nested = walk(node.children)
      if (nested) return nested
    }
    return null
  }

  return walk(menus)
}

/** 判断导航菜单项是否为产品中心菜单（产品式 / 入口卡片；菜单式产品栏目走普通子菜单） */
export function isProductsMenu(menu) {
  if (!menu || menu.isHome) return false
  if (isProductCatalogTemplateId(menu.templateId)) return true
  // 兼容旧数据：产品类型且未显式配置其它模板时按产品式处理
  if (menu.contentType !== 'PRODUCT') return false
  if (menu.templateId == null || menu.templateId === '') return true
  return isProductCatalogTemplateId(resolveTemplateId(menu.templateId))
}

/** 判断导航菜单项是否为解决方案菜单 */
export function isSolutionsMenu(menu) {
  if (!menu || menu.isHome) return false
  if (resolveTemplateId(menu.templateId) === 2) return true
  return menu.contentType === 'SOLUTION'
}

/** 二级菜单是否竖向排列（仅产品指南） */
export function isVerticalSubnavMenu(menu) {
  if (!menu || menu.isHome) return false
  return isProductsMenu(menu)
}

/** 构建产品分类页路由路径 */
export function buildProductCategoryPath(categoryId, productsRootPath = '') {
  const base = normalizeMenuNodePath(productsRootPath)
  if (!base) return `/products/category/${categoryId}`
  return `${base}/category/${categoryId}`
}

/** 将产品分类转换为导航子菜单项（递归保留全部子级） */
function transformProductCategoryChild(category, productsRootPath) {
  const children = (category.children || [])
    .filter((child) => child.enabled !== false)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((child) => transformProductCategoryChild(child, productsRootPath))

  return {
    id: category.id,
    name: category.name,
    path: buildProductCategoryPath(category.id, productsRootPath),
    children
  }
}

/** 将产品分类列表合并到导航菜单的产品中心子项 */
export function mergeProductNavChildren(menus = [], categories = []) {
  if (!categories.length) return menus

  return menus.map((menu) => {
    if (!isProductsMenu(menu)) return menu

    const productsRootPath = normalizePath(menu.defaultPath)
    const children = categories.map((category) => transformProductCategoryChild(category, productsRootPath))

    return {
      ...menu,
      children,
      match: buildMatch(productsRootPath, children)
    }
  })
}

function navNodeContainsPath(node, path) {
  const current = String(path || '')
  if (!node?.path || !current) return false
  if (current === node.path || current.startsWith(`${node.path}/`)) return true
  return (node.children || []).some((child) => navNodeContainsPath(child, current))
}

/** 在产品二级列表中查找包含当前路径的项 */
export function findNavChildContainingPath(children = [], path) {
  return children.find((child) => navNodeContainsPath(child, path)) || null
}

function toNavPathList(siblings = []) {
  return siblings.map((item) => (typeof item === 'string' ? item : item?.path))
}

/**
 * 子菜单高亮：空 path / 首页不高亮；与父级栏目 path 相同不高亮；同级取最长匹配。
 */
export function isNavChildPathActive(currentPath, itemPath, siblingPaths = [], parentPath = '') {
  if (!isPathUnderMenu(currentPath, itemPath)) return false

  const item = normalizeMenuNodePath(itemPath)
  const parent = normalizeMenuNodePath(parentPath)
  if (parent && parent !== HOME_PATH && item === parent) return false

  const siblings = toNavPathList(siblingPaths)
  const candidates = siblings.length ? siblings : [itemPath]
  let best = ''
  for (const path of candidates) {
    if (!isPathUnderMenu(currentPath, path)) continue
    const normalized = normalizeMenuNodePath(path)
    if (parent && parent !== HOME_PATH && normalized === parent) continue
    if (normalized.length > best.length) best = normalized
  }

  return item === best
}
